import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiAlertTriangle,
  FiTrash2,
  FiLock,
  FiEye,
  FiEyeOff,
  FiX,
  FiLink,
  FiShield
} from "react-icons/fi";
import { Vault, LinkedProgramInfo } from "../../utils/api/endpoints/vault";
import {
  decryptPrivateKey,
  decryptCredentialUnified
} from "../../utils/crypto/vault-crypto";

interface DeleteVaultModalProps {
  isOpen: boolean;
  onClose: () => void;
  vault: Vault | null;
  allVaults: Vault[];
  sessionMasterPassword?: string | null;
  linkedPrograms?: LinkedProgramInfo[];
  isLoadingLinkedPrograms?: boolean;
  onConfirmDelete: (vaultId: number) => Promise<void>;
  hasMasterPasswordBeenSet: boolean;
}

const DeleteVaultModal: React.FC<DeleteVaultModalProps> = ({
  isOpen,
  onClose,
  vault,
  allVaults,
  sessionMasterPassword,
  linkedPrograms = [],
  isLoadingLinkedPrograms = false,
  onConfirmDelete,
  hasMasterPasswordBeenSet
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens or closes
  useEffect(() => {
    setPassword("");
    setShowPassword(false);
    setIsDeleting(false);
    setError(null);
  }, [isOpen, vault]);

  // Handle escape and outside clicks
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target as Node) &&
        !isDeleting
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, isDeleting, onClose]);

  if (!isOpen || !vault) return null;

  const isLinked = linkedPrograms.length > 0;
  const credentialCount = vault.entries?.length || 0;

  /**
   * Cryptographically verifies the entered master password client-side
   * following zero-knowledge architecture.
   */
  const verifyMasterPassword = async (candidatePassword: string): Promise<boolean> => {
    // 1. If session already has an unlocked master password, compare directly
    if (sessionMasterPassword && candidatePassword !== sessionMasterPassword) {
      return false;
    }

    // 2. Cryptographic check: Attempt to decrypt this vault's private key
    if (
      vault.encrypted_private_key &&
      vault.private_key_iv &&
      vault.private_key_salt &&
      vault.private_key_auth_tag
    ) {
      try {
        await decryptPrivateKey(
          {
            encrypted_private_key: vault.encrypted_private_key,
            private_key_iv: vault.private_key_iv,
            private_key_salt: vault.private_key_salt,
            private_key_auth_tag: vault.private_key_auth_tag
          },
          candidatePassword
        );
        return true;
      } catch {
        return false;
      }
    }

    // 3. Attempt to decrypt first symmetric credential in this vault
    if (vault.entries && vault.entries.length > 0) {
      const saltedEntry = vault.entries.find(
        (e) => e.salt && e.encrypted_data && e.iv && e.auth_tag
      );
      if (saltedEntry) {
        try {
          await decryptCredentialUnified(
            {
              entry_id: saltedEntry.entry_id,
              encrypted_data: saltedEntry.encrypted_data,
              iv: saltedEntry.iv,
              auth_tag: saltedEntry.auth_tag,
              salt: saltedEntry.salt,
              key_iterations: saltedEntry.key_iterations,
              encrypted_credential_key: saltedEntry.encrypted_credential_key
            },
            candidatePassword,
            vault.encrypted_private_key
              ? {
                  encrypted_private_key: vault.encrypted_private_key,
                  private_key_iv: vault.private_key_iv!,
                  private_key_salt: vault.private_key_salt!,
                  private_key_auth_tag: vault.private_key_auth_tag!
                }
              : undefined
          );
          return true;
        } catch {
          return false;
        }
      }
    }

    // 4. Fallback check: Test against another vault in workspace that has encrypted RSA keys
    const otherVaultWithKeys = allVaults.find(
      (v) =>
        v.vault_id !== vault.vault_id &&
        v.encrypted_private_key &&
        v.private_key_iv &&
        v.private_key_salt &&
        v.private_key_auth_tag
    );
    if (otherVaultWithKeys) {
      try {
        await decryptPrivateKey(
          {
            encrypted_private_key: otherVaultWithKeys.encrypted_private_key!,
            private_key_iv: otherVaultWithKeys.private_key_iv!,
            private_key_salt: otherVaultWithKeys.private_key_salt!,
            private_key_auth_tag: otherVaultWithKeys.private_key_auth_tag!
          },
          candidatePassword
        );
        return true;
      } catch {
        return false;
      }
    }

    // 5. If session master password was present and matched
    if (sessionMasterPassword) {
      return candidatePassword === sessionMasterPassword;
    }

    // If no vault or credential has any encrypted private key or salt, allow pass
    return true;
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isLinked) {
      setError("Please unlink this vault from all programs before deleting.");
      return;
    }

    // If master password is required, validate input
    if (hasMasterPasswordBeenSet) {
      if (!password.trim()) {
        setError("Master password is required to delete this vault.");
        return;
      }

      setIsDeleting(true);
      try {
        const isValid = await verifyMasterPassword(password);
        if (!isValid) {
          setError("Incorrect Master Password. Please check your password and try again.");
          setIsDeleting(false);
          return;
        }
      } catch (err: any) {
        setError("Failed to verify master password: " + (err.message || "Unknown error"));
        setIsDeleting(false);
        return;
      }
    } else {
      setIsDeleting(true);
    }

    try {
      await onConfirmDelete(vault.vault_id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to delete vault.");
    } finally {
      setIsDeleting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 transition-all">
      <div
        ref={modalRef}
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-slate-900 px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-500/20 text-red-400 rounded-xl border border-red-500/30">
              <FiTrash2 className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white tracking-tight">
                {isLinked ? "Cannot Delete Vault" : "Delete Credential Vault"}
              </h3>
              <p className="text-xs text-slate-400">
                {isLinked ? "Active program links detected" : "Irreversible security action"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {/* Target Vault Summary Card */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Target Vault
                </span>
                <h4 className="text-base font-bold text-slate-900 mt-0.5">
                  {vault.name}
                </h4>
                {vault.description && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {vault.description}
                  </p>
                )}
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-200 text-slate-700">
                <FiShield className="w-3.5 h-3.5 text-slate-500" />
                {credentialCount} {credentialCount === 1 ? "credential" : "credentials"}
              </span>
            </div>
          </div>

          {/* Conditional Display: Linked Programs Blocking vs Password Form */}
          {isLoadingLinkedPrograms ? (
            <div className="py-8 flex flex-col items-center justify-center gap-3 text-slate-400">
              <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs font-medium">Checking program dependencies...</p>
            </div>
          ) : isLinked ? (
            /* Blocked state when linked to programs */
            <div className="space-y-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                <FiAlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  <p className="font-bold text-amber-950 mb-1">
                    Unlink Required Before Deletion
                  </p>
                  This vault is currently linked to <strong>{linkedPrograms.length}</strong> active program(s).
                  To maintain audit integrity and prevent broken test environments, you must unlink this vault from all programs first.
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Active Links ({linkedPrograms.length})
                </label>
                <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden bg-white">
                  {linkedPrograms.map((lp) => (
                    <div
                      key={lp.programId}
                      className="px-4 py-3 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2.5 font-semibold text-slate-800">
                        <FiLink className="w-4 h-4 text-slate-400" />
                        <span>{lp.programTitle}</span>
                      </div>
                      <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                        Connected
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Unlinked state: Show Warning & Master Password Prompt */
            <form id="delete-vault-form" onSubmit={handleDelete} className="space-y-4">
              {/* Danger Warning Box */}
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                <FiAlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-900 leading-relaxed">
                  <p className="font-bold text-red-950 mb-1">
                    Critical Security Notice
                  </p>
                  Deleting this vault will permanently delete all <strong>{credentialCount}</strong> credential(s) contained within it.
                  This action is permanent and <strong>cannot be undone</strong>.
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700 animate-in fade-in">
                  {error}
                </div>
              )}

              {/* Master Password Input */}
              {hasMasterPasswordBeenSet ? (
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="delete-master-password"
                      className="text-xs font-bold text-slate-700 flex items-center gap-1.5"
                    >
                      <FiLock className="w-3.5 h-3.5 text-slate-400" />
                      Enter Master Password to Confirm
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      id="delete-master-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(null);
                      }}
                      placeholder="Enter your master password"
                      autoFocus
                      disabled={isDeleting}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pr-11 text-sm text-slate-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-normal">
                    Zero-knowledge cryptographic verification: Your master password will be validated client-side before authorization.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  No Master Password is set for this workspace. Click Confirm below to proceed with deletion.
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-xs font-bold text-slate-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {isLinked ? "Close" : "Cancel"}
          </button>
          {!isLinked && (
            <button
              type="submit"
              form="delete-vault-form"
              disabled={isDeleting || isLoadingLinkedPrograms || (hasMasterPasswordBeenSet && !password.trim())}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-xs font-bold text-white hover:bg-red-700 transition-all shadow-md shadow-red-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Verifying & Deleting...</span>
                </>
              ) : (
                <>
                  <FiTrash2 className="w-3.5 h-3.5" />
                  <span>Delete Vault Permanently</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteVaultModal;
